import { MARK_PATH, MARK_VIEWBOX } from '../ui/logoPath';

interface WatermarkProps {
  /**
   * Where the mark sits. 'hero' is the restrained treatment: anchored to the
   * top-right corner at a size that reads as an accent, the way the footer
   * mark does — not the full-bleed wedge that dominates the section.
   */
  position?: 'hero' | 'bottom-left' | 'right' | 'bottom-right';
  /** Mark height as a fraction of the container's height. */
  scale?: number;
  opacity?: number;
  className?: string;
}

/**
 * The oversized, low-opacity mark bleeding off-canvas — the treatment from the
 * client's LinkedIn banner.
 *
 * Used only in the footer and on dark CTA / metrics bands, deliberately not on
 * every page hero, so it stays a considered accent rather than wallpaper.
 *
 * Always decorative: aria-hidden and pointer-events-none, and layered beneath
 * content so it never reduces text contrast below AA.
 */
export function Watermark({
  position = 'right',
  scale = 1.35,
  opacity = 0.05,
  className = '',
}: WatermarkProps) {
  const place =
    position === 'hero'
      ? // Tucked into the top-right, clear of the headline on the left.
        'right-[3%] top-[14%]'
      : position === 'bottom-left'
        ? '-left-[8%] -bottom-[22%]'
        : position === 'bottom-right'
          ? '-right-[6%] -bottom-[20%]'
          : '-right-[4%] top-1/2 -translate-y-1/2';

  /*
    The mark's edges are straight and hard, so at full opacity it reads as a
    wedge cutting across the background rather than as texture. A soft radial
    mask fades its outer edges into the surface.
  */
  const fade =
    position === 'hero'
      ? 'radial-gradient(150% 150% at 100% 0%, black 45%, transparent 92%)'
      : position === 'bottom-left'
        ? 'radial-gradient(140% 140% at 0% 100%, black 55%, transparent 96%)'
        : position === 'bottom-right'
          ? 'radial-gradient(140% 140% at 100% 100%, black 55%, transparent 96%)'
          : 'radial-gradient(135% 135% at 100% 50%, black 52%, transparent 95%)';

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute select-none ${place} ${className}`}
      style={{
        height: `${scale * 100}%`,
        opacity,
        maskImage: fade,
        WebkitMaskImage: fade,
      }}
    >
      <svg
        viewBox={MARK_VIEWBOX}
        className="h-full w-auto"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path d={MARK_PATH} />
      </svg>
    </div>
  );
}
